import { useState } from 'react';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'motion/react';
import type { OnboardingBusinessProfile } from '../../../model/types';
import FigmaStepModal from '../../modal/FigmaStepModal';
import { ONBOARDING_STEP_LABELS, ONBOARDING_TOTAL_STEPS } from '../../../model/constants';

interface BusinessProfileStepProps {
  value: OnboardingBusinessProfile;
  isSaving: boolean;
  error?: string | null;
  onContinue: (value: OnboardingBusinessProfile) => Promise<void>;
  onSaveAndExit: (draft: OnboardingBusinessProfile) => Promise<void>;
}

interface LocalBusinessQAItem {
  id: string;
  question: string;
  answer: string;
}

const FIELD_CONFIG: Array<{
  key: keyof OnboardingBusinessProfile;
  label: string;
  placeholder: string;
  helper: string;
}> = [
  {
    key: 'whatYouDo',
    label: 'מה אתם עושים?',
    placeholder: 'ספרו לCloser.AI מה אתם עושים...',
    helper: 'ספרו בהרחבה על הפעילות של העסק שלכם, השיטה והאני מאמין שלכם',
  },
  {
    key: 'whyChooseYou',
    label: 'למה לבחור דווקא בכם?',
    placeholder: 'ספרו לCloser.AI למה לבחור דווקא בכם...',
    helper: 'למה לקוח פוטנציאלי ירצה לקנות דווקא מכם ולא מהמתחרים? מה מבדל אתכם מהם?',
  },
  {
    key: 'productDetails',
    label: 'פרטים על המוצר',
    placeholder: 'ספרו לCloser AI על המוצרים והשירותים שלכם...',
    helper: 'ספרו על המוצרים והשירותים שקיימים בעסק שלכם (מאפיינים עיקריים, החבילות והשירותים שאתם מציעים):',
  },
  {
    key: 'leadSourcesAndGoal',
    label: 'מאיפה הלידים מגיעים ומה מטרת השיחה?',
    placeholder: 'ספרו לCloser.AI מאיפה הלידים מגיעים ומה מטרת השיחה...',
    helper: 'ספרו מה הדרך שהליד עובר לפני שאיש המכירות מדבר איתו בטלפון. האם הליד הגיע מקמפיין ממומן או מתוכן אורגני, באיזה פלטפורמה הוא נחשף אליכם, מה הליד עבר לפני שיחת הטלפון עם איש המכירות ומה המטרה של השיחה (למכור ללקוח, לקבוע פגישה, משהו אחר?)',
  },
  {
    key: 'commonQuestionsAndAnswers',
    label: 'שאלות ותשובות נפוצות (רשות)',
    placeholder: '',
    helper: 'אלו שאלות לקוחות פוטנציאלים שואלים על העסק בשיחות המכירה וכדאי שאיש המכירות יכיר?',
  },
];

const createEmptyQAItem = (): LocalBusinessQAItem => ({
  id: `qa-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  question: '',
  answer: '',
});

const parseBusinessQAString = (value: string): LocalBusinessQAItem[] => {
  const normalized = value.trim();
  if (!normalized) {
    return [createEmptyQAItem()];
  }

  const blocks = normalized.split(/\n{2,}/).map((block) => block.trim()).filter(Boolean);
  const parsedItems = blocks
    .map((block) => {
      const questionMatch = block.match(/question:\s*(.+)/i) ?? block.match(/q:\s*(.+)/i);
      const answerMatch = block.match(/answer:\s*([\s\S]+)/i) ?? block.match(/a:\s*([\s\S]+)/i);

      if (!questionMatch && !answerMatch) {
        return {
          id: createEmptyQAItem().id,
          question: '',
          answer: block,
        };
      }

      return {
        id: createEmptyQAItem().id,
        question: questionMatch?.[1]?.trim() ?? '',
        answer: answerMatch?.[1]?.trim() ?? '',
      };
    })
    .filter((item) => item.question.trim() || item.answer.trim());

  return parsedItems.length > 0 ? parsedItems : [createEmptyQAItem()];
};

const serializeBusinessQAItems = (items: LocalBusinessQAItem[]): string =>
  items
    .filter((item) => item.question.trim() && item.answer.trim())
    .map((item) => `Question: ${item.question.trim()}\nAnswer: ${item.answer.trim()}`)
    .join('\n\n');

const BusinessProfileStep: React.FC<BusinessProfileStepProps> = ({
  value,
  isSaving,
  error,
  onContinue,
  onSaveAndExit,
}) => {
  const [formState, setFormState] = useState<OnboardingBusinessProfile>(value);
  const [errors, setErrors] = useState<Partial<Record<keyof OnboardingBusinessProfile, string>>>({});
  const [currentFieldIndex, setCurrentFieldIndex] = useState<number>(() => {
    const firstIncompleteIndex = FIELD_CONFIG.findIndex(({ key }) => !value[key]?.trim());
    return firstIncompleteIndex === -1 ? FIELD_CONFIG.length - 1 : firstIncompleteIndex;
  });
  const [animationDirection, setAnimationDirection] = useState(1);
  const [qaItems, setQaItems] = useState<LocalBusinessQAItem[]>(() => parseBusinessQAString(value.commonQuestionsAndAnswers));

  const currentField = FIELD_CONFIG[currentFieldIndex];
  const isLastQuestion = currentFieldIndex === FIELD_CONFIG.length - 1;
  const isQAField = currentField.key === 'commonQuestionsAndAnswers';
  const serializedQAValue = serializeBusinessQAItems(qaItems);

  const syncQAItems = (nextItems: LocalBusinessQAItem[]) => {
    setQaItems(nextItems);
    setFormState((current) => ({
      ...current,
      commonQuestionsAndAnswers: serializeBusinessQAItems(nextItems),
    }));
    setErrors((current) => ({ ...current, commonQuestionsAndAnswers: undefined }));
  };

  const handleNext = async () => {
    const currentValue = isQAField ? serializedQAValue : formState[currentField.key];

    if (!isQAField && !currentValue.trim()) {
      setErrors((current) => ({ ...current, [currentField.key]: 'שדה חובה' }));
      return;
    }

    if (!isLastQuestion) {
      setAnimationDirection(1);
      setCurrentFieldIndex((current) => current + 1);
      return;
    }

    const nextErrors = FIELD_CONFIG.reduce<Partial<Record<keyof OnboardingBusinessProfile, string>>>(
      (accumulator, field) => {
        if (field.key === 'commonQuestionsAndAnswers') return accumulator;

        if (!formState[field.key].trim()) {
          accumulator[field.key] = 'שדה חובה';
        }
        return accumulator;
      },
      {}
    );

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      const firstIncompleteIndex = FIELD_CONFIG.findIndex(({ key }) => nextErrors[key]);
      if (firstIncompleteIndex !== -1) {
        setAnimationDirection(firstIncompleteIndex < currentFieldIndex ? -1 : 1);
        setCurrentFieldIndex(firstIncompleteIndex);
      }
      return;
    }

    try {
      await onContinue(formState);
    } catch {
      // Error surface is handled by the shared onboarding error area.
    }
  };

  const handlePrevious = async () => {
    if (currentFieldIndex > 0) {
      setAnimationDirection(-1);
      setCurrentFieldIndex((current) => current - 1);
      return;
    }

    await onSaveAndExit(formState);
  };

  const primaryAction = (
    <button
      type="button"
      onClick={() => void handleNext()}
      disabled={isSaving}
      className="inline-flex items-center gap-3 rounded-full bg-gradient-to-l from-brand-400 to-brand-700 px-10 py-[14px] text-base font-bold text-white shadow-[0px_20px_25px_-5px_rgba(173,51,10,0.2),0px_8px_10px_-6px_rgba(173,51,10,0.2)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70"
    >
      <span>{isLastQuestion ? 'צור פרופיל עסק מותאם אישית' : 'לשאלה הבאה'}</span>
      <ArrowLeftIcon className="h-4 w-4" />
    </button>
  );

  const secondaryAction = (
    <button
      type="button"
      onClick={() => void handlePrevious()}
      className="rounded-full px-6 py-[14px] text-base font-bold text-text-800 transition hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-700"
    >
      {currentFieldIndex > 0 ? 'לשאלה הקודמת' : 'שלב הקודם'}
    </button>
  );

  return (
    <FigmaStepModal
      embedded
      stepNumber={2}
      totalSteps={ONBOARDING_TOTAL_STEPS}
      stepLabels={[...ONBOARDING_STEP_LABELS]}
      title="התאמה אישית"
      description="ספרו לבינה המלאכותית על העסק שלכם (כתבו כאילו אתם מסבירים לנציג מכירות חדש)."
      error={error}
      showBadge={false}
      titleBelow
      footerPrimary={primaryAction}
      footerSecondary={secondaryAction}
    >
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between gap-3">

          <AnimatePresence mode="wait">
            <motion.span
              key={currentFieldIndex}
              initial={{ scale: 0.75, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.75, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="shrink-0 inline-flex items-center rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white shadow-sm"
            >
              {currentFieldIndex + 1}/{FIELD_CONFIG.length}
            </motion.span>
          </AnimatePresence>

          <h3 className="text-right text-lg font-bold text-text-800 dark:text-neutral-200">
            {currentField.label}
          </h3>
        </div>

        <div className="min-h-[252px]">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={currentField.key}
              initial={{ opacity: 0, x: animationDirection > 0 ? -24 : 24, y: 8 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={{ opacity: 0, x: animationDirection > 0 ? 24 : -24, y: -8 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col gap-3"
            >
              <p className="text-right text-sm leading-6 text-text-600 dark:text-neutral-400">
                {currentField.helper}
              </p>

              {isQAField ? (
                <div className={`space-y-4 rounded-2xl border bg-neutral-100/80 p-4 dark:bg-neutral-700/30 ${
                  errors[currentField.key] ? 'border-error-400 dark:border-error-600' : 'border-neutral-200 dark:border-neutral-600'
                }`}>
                  {qaItems.map((qaItem, index) => (
                    <div
                      key={qaItem.id}
                      className="relative rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
                    >
                      {qaItems.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => syncQAItems(qaItems.filter((item) => item.id !== qaItem.id))}
                          className="absolute left-3 top-3 rounded-lg p-1 text-neutral-400 transition hover:bg-neutral-100 hover:text-error-500 dark:hover:bg-neutral-700"
                          aria-label="Remove question and answer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      ) : null}

                      <div className="flex items-start gap-3">
                        <div className="flex-1 space-y-3">
                          <div>
                            <label className="mb-2 block text-right text-xs font-bold uppercase tracking-[0.18em] text-text-700 dark:text-neutral-400">
                              שאלה {index + 1}
                            </label>
                            <input
                              autoFocus={index === 0}
                              value={qaItem.question}
                              onChange={(event) => {
                                const nextItems = qaItems.map((item) =>
                                  item.id === qaItem.id ? { ...item, question: event.target.value } : item
                                );
                                syncQAItems(nextItems);
                              }}
                              className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-right text-sm text-neutral-900 outline-none placeholder:text-text-500 focus:border-brand-400 dark:border-neutral-600 dark:bg-neutral-700 dark:text-neutral-100 dark:placeholder:text-neutral-400"
                              placeholder=""
                            />
                          </div>

                          <div>
                            <label className="mb-2 block text-right text-xs font-bold uppercase tracking-[0.18em] text-text-700 dark:text-neutral-400">
                              תשובה
                            </label>
                            <textarea
                              value={qaItem.answer}
                              onChange={(event) => {
                                const nextItems = qaItems.map((item) =>
                                  item.id === qaItem.id ? { ...item, answer: event.target.value } : item
                                );
                                syncQAItems(nextItems);
                              }}
                              rows={3}
                              className="w-full resize-none rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-right text-sm leading-6 text-neutral-900 outline-none placeholder:text-text-500 focus:border-brand-400 dark:border-neutral-600 dark:bg-neutral-700 dark:text-neutral-100 dark:placeholder:text-neutral-400"
                              placeholder=""
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => syncQAItems([...qaItems, createEmptyQAItem()])}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-brand-300 px-4 py-3 text-sm font-bold text-brand-700 transition hover:bg-brand-50 dark:border-brand-700 dark:text-brand-300 dark:hover:bg-brand-900/20"
                  >
                    <PlusIcon className="h-4 w-4" />
                    <span>הוסף שאלה ותשובה</span>
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <textarea
                    autoFocus
                    value={formState[currentField.key]}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setFormState((current) => ({ ...current, [currentField.key]: nextValue }));
                      setErrors((current) => ({ ...current, [currentField.key]: undefined }));
                    }}
                    rows={5}
                    className={`w-full resize-none rounded-xl border bg-neutral-100 px-4 py-4 text-right text-base leading-6 text-neutral-900 outline-none placeholder:text-text-500 dark:bg-neutral-700 dark:text-neutral-100 dark:placeholder:text-neutral-400 ${
                      errors[currentField.key] ? 'border-error-400 dark:border-error-600' : 'border-neutral-200 dark:border-neutral-600'
                    }`}
                    placeholder={currentField.placeholder}
                  />
                </div>
              )}

              {errors[currentField.key] ? (
                <p className="text-right text-sm text-error-600 dark:text-error-400">{errors[currentField.key]}</p>
              ) : null}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </FigmaStepModal>
  );
};

export default BusinessProfileStep;
